import { Box, Button, CircularProgress, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  rateConfirmationAction,
  creteNotificationAction,
} from "../../redux/action";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { userSelector } from "../../../user-redux/selector";
import { myCandidatesSelector } from "../../redux/selector";

const savedRateValue = (rate) => {
  if (rate == null) return "";
  const value = String(rate).trim();
  if (!value || Number(value) === 0) return "";
  return value;
};

// Rate Confirmation
// onRateSaved: parent returns to the live workflow step after save (e.g. Schedule Interview)
const Step5 = ({ onRateSaved }) => {
  const dispatch = useDispatch();

  const { candidateDetails, jobDetails, selectedSubmission, submissionStatus } =
    useSelector(myCandidatesSelector);

  const [textFieldValue, setTextFieldValue] = useState(() =>
    savedRateValue(selectedSubmission?.s_rate)
  );

  useEffect(() => {
    setTextFieldValue(savedRateValue(selectedSubmission?.s_rate));
  }, [selectedSubmission?.submission_id, selectedSubmission?.s_rate]);

  const statusMatchesSelected =
    submissionStatus?.candidate_id != null &&
    submissionStatus?.job_id != null &&
    selectedSubmission?.candidate_id != null &&
    selectedSubmission?.job_id != null &&
    Number(submissionStatus.candidate_id) ===
      Number(selectedSubmission.candidate_id) &&
    Number(submissionStatus.job_id) === Number(selectedSubmission.job_id);
  const benchStatusId = Number(
    (statusMatchesSelected
      ? submissionStatus?.bench_status_id
      : null) ?? selectedSubmission?.bench_status_id
  );
  // Editable after rate step is reached, until interview is scheduled (status 8+)
  const isTerminalStatus = ["Selected", "Rejected"].includes(
    selectedSubmission?.pStatusName
  );
  const canEditRate =
    Number.isFinite(benchStatusId) &&
    benchStatusId >= 5 &&
    benchStatusId < 8 &&
    !isTerminalStatus;
  const rateLocked = !canEditRate;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { user } = useSelector(userSelector);
  const handleOpenSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleChange = (event) => {
    setTextFieldValue(event.target.value);
  };

  // Status 5 = on Rate Confirmation (first submit advances to 7).
  // Status 7+ = rate already confirmed; Update saves rate only (no step change).
  const isRateEdit = Number.isFinite(benchStatusId) && benchStatusId >= 7;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rateLocked) return;

    const submissionId = selectedSubmission?.submission_id;
    if (submissionId == null) {
      handleOpenSnackbar("Missing submission id — cannot update rate", "error");
      return;
    }

    setIsSubmitting(true);
    const rate = {
      rate: String(textFieldValue || "").trim(),
      submissionid: submissionId,
    };
    // First confirmation advances to Schedule Interview; later edits keep status
    const updatedData = isRateEdit
      ? null
      : {
          candidateid: selectedSubmission?.candidate_id,
          jobid: selectedSubmission?.job_id,
          userid: selectedSubmission?.user_id,
          status: "7",
        };
    try {
      const result = await dispatch(
        rateConfirmationAction(
          rate,
          updatedData,
          selectedSubmission?.user_id,
          selectedSubmission?.candidate_id,
          selectedSubmission?.job_id,
          { rateOnly: isRateEdit }
        )
      );
      if (!result) {
        throw new Error("Rate update did not complete");
      }
      handleOpenSnackbar(
        isRateEdit
          ? "Rate updated successfully!"
          : "Rate Submitted successfully!",
        "success"
      );
      if (!isRateEdit) {
        const notifyData = {
          user_id: user?.id,
          notifiedto: candidateDetails?.candidate_id,
          notificationDescription: " Recuiter Has Confirmed The Rate For",
          notificationSubject: "RateConfirmed",
          notificationType: "Candidate Notification",
          job_id: jobDetails?.id,
          candidate_name: candidateDetails?.candidate_name,
          job_title: jobDetails?.jobtitle,
        };
        await dispatch(creteNotificationAction(notifyData));
      }
      // After save, return to the current status step (Schedule Interview when status is 7)
      if (typeof onRateSaved === "function") {
        onRateSaved({ isRateEdit, benchStatusId });
      }
    } catch (error) {
      console.error(error);
      handleOpenSnackbar(
        error?.message || "something went wrong!",
        "error"
      );
    }

    setIsSubmitting(false);
  };
  return (
    <div style={{ padding: "16px" }}>
      <TextField
        fullWidth
        label={
          rateLocked
            ? "Submitted Rate"
            : isRateEdit
              ? "Update Rate"
              : "Enter Rate"
        }
        variant="outlined"
        placeholder="$"
        sx={{ marginBottom: 2 }}
        value={textFieldValue}
        onChange={handleChange}
        disabled={rateLocked}
      />

      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ width: 150 }}
          onClick={(e) => handleSubmit(e)}
          disabled={rateLocked || !String(textFieldValue || "").trim()}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : isRateEdit ? (
            "Update"
          ) : (
            "Submit"
          )}
        </Button>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default Step5;
